import React, { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { listUserLessons, listLessons } from "../../graphql/queries";
import { deleteUserLesson, updateUserLesson, createUserLesson } from "../../graphql/mutations";
import { useNavigate } from "react-router-dom";
import { 
  Box, 
  Typography, 
  IconButton, 
  Snackbar, 
  Alert, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  Button,
  TextField,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import UserLessonsTable from "./UserLessonsTable";
import CreateUserLessonModal from "./CreateUserLessonModal";
import BulkActionsToolbar from "./BulkActionsToolbar";
import BulkDeleteConfirmDialog from "./BulkDeleteConfirmDialog";
import BulkStatusChangeModal from "./BulkStatusChangeModal";
import { findDuplicateUserLessons, invalidateAllUserLessonCaches } from "../../utils/userLessonUtils";
import "./ManageUserLessons.css";

// We now load ALL matching records in a single search operation (looping pages under the hood)
const PAGE_LIMIT = 1000; // Max page size per GraphQL request (adjust if your API enforces a smaller max)
// Removed MAX_AUTO_LOAD_ATTEMPTS: server-side filtering eliminates need for auto-load attempts

const ManageUserLessons = () => {
  const navigate = useNavigate();
  const [userLessons, setUserLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  // Removed incremental pagination (loadingMore state not needed)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [open, setOpen] = useState(false);
  const [userLessonToDelete, setUserLessonToDelete] = useState(null);
  // Removed nextToken / hasMore state (no pagination)
  const [lessonTitleMap, setLessonTitleMap] = useState({});
  const [lessonsOptions, setLessonsOptions] = useState([]);
  // Removed autoLoadAttempts state (no longer needed with server-side filtering)
  const [totalRecords, setTotalRecords] = useState(0);
  const [isAllDataLoaded, setIsAllDataLoaded] = useState(true); // default true until a search is run
  
  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    lessonID: ''
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [currentSearchFilters, setCurrentSearchFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    lessonID: ''
  });
  const [lastSearchCriteria, setLastSearchCriteria] = useState(null);
  const [searchCriteriaModified, setSearchCriteriaModified] = useState(false);

  // New state for bulk actions and create modal
  const [selectedUserLessonIds, setSelectedUserLessonIds] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Helper function to compare search criteria
  const searchCriteriaChanged = (newFilters, currentFilters) => {
    return (
      newFilters.firstName !== currentFilters.firstName ||
      newFilters.lastName !== currentFilters.lastName ||
      newFilters.email !== currentFilters.email ||
      newFilters.lessonID !== currentFilters.lessonID
    );
  };

  // Helper function to create search criteria string for comparison
  const getSearchCriteriaKey = (filters) => {
    return `${filters.firstName}|${filters.lastName}|${filters.email}|${filters.lessonID}`;
  };

  useEffect(() => {
    const fetchLessons = async () => {
      const client = generateClient();
      try {
        // Fetch all lessons to get titles (cache this)
        const lessonsResult = await client.graphql({
          query: listLessons,
          variables: {
            limit: 1000,
          },
        });

        const lessonsData = lessonsResult.data.listLessons.items;
        const titleMap = lessonsData.reduce((map, lesson) => {
          map[lesson.id] = lesson.title;
          return map;
        }, {});
        
        // Create options array for the Autocomplete
        const options = lessonsData.map(lesson => ({
          id: lesson.id,
          title: lesson.title,
          label: lesson.title
        }));
        
        setLessonTitleMap(titleMap);
        setLessonsOptions(options);
      } catch (error) {
        console.error("Error fetching lessons:", error);
      }
    };

    fetchLessons();
  }, []);

  const handleSearchInputChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark criteria as modified if we have searched before
    if (hasSearched) {
      setSearchCriteriaModified(true);
    }
  };

  const handleSearch = async () => {
    if (Object.keys(lessonTitleMap).length === 0) {
      setSnackbarMessage("Esperando a que se carguen las lecciones...");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

  // Detect if search criteria have changed
  const criteriaChanged = searchCriteriaChanged(searchFilters, currentSearchFilters);
    const newSearchCriteriaKey = getSearchCriteriaKey(searchFilters);
    
    // Prevent duplicate searches if already loading or if we're doing the same search and all data is loaded
  if (loading) {
      return;
    }

    // If criteria changed, automatically reset and show message
    if (criteriaChanged && hasSearched && userLessons.length > 0) {
      setSnackbarMessage("Criterios de búsqueda modificados. Iniciando nueva búsqueda...");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
    }

    // Check if we're trying to search with the same criteria when all data is already loaded
  if (!criteriaChanged && isAllDataLoaded && lastSearchCriteria === newSearchCriteriaKey) {
      setSnackbarMessage("Ya se han cargado todos los registros para esta búsqueda.");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
      return;
    }
  // Reset for new search
  setLoading(true);
  setUserLessons([]);
  // pagination cleared
  setHasSearched(true);
  setIsAllDataLoaded(false); // will set true after loop completes
  setTotalRecords(0);
  setLastSearchCriteria(newSearchCriteriaKey);
  setSearchCriteriaModified(false);
  setCurrentSearchFilters(searchFilters);

    const client = generateClient();

    // Helper to build server-side GraphQL filter
    const buildServerFilter = (filters) => {
      const filter = {};
      // NOTE: AppSync/Dynamo filters are case-sensitive. For now we use 'contains' to allow partial matches.
      if (filters.firstName) filter.firstName = { contains: filters.firstName };
      if (filters.lastName) filter.lastName = { contains: filters.lastName };
      if (filters.email) filter.email = { contains: filters.email }; // could switch to eq for exact
      if (filters.lessonID) filter.lessonID = { eq: filters.lessonID };
      return Object.keys(filter).length ? filter : undefined;
    };

    try {
      // Use current search filters for load more, or new search filters for new search
  const activeFilters = searchFilters;
      
      // Loop through all pages accumulating results
  let aggregated = [];
  let localNextToken = null;
      do {
        const variables = {
          limit: PAGE_LIMIT,
            // use undefined filter to avoid sending empty object which may break some resolvers
          filter: buildServerFilter(activeFilters),
          nextToken: localNextToken
        };
        const res = await client.graphql({ query: listUserLessons, variables });
        const pageItems = (res?.data?.listUserLessons?.items) || [];
        aggregated = aggregated.concat(pageItems);
        localNextToken = res?.data?.listUserLessons?.nextToken || null;
  // continue until no nextToken
      } while (localNextToken);

      let userLessonsData = aggregated;
  // all data fetched

      // Sort by updatedAt descending (newer first)
      userLessonsData.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.createdAt || 0);
        return dateB - dateA;
      });

      // Detect and mark duplicates using utility function
      const duplicateGroups = findDuplicateUserLessons(userLessonsData);
      const totalDuplicates = Object.values(duplicateGroups).reduce(
        (sum, group) => sum + group.length - 1, 0
      );

      // Log duplicates for admin awareness
      if (totalDuplicates > 0) {
        Object.entries(duplicateGroups).forEach(([key, duplicates]) => {
          console.warn(`Found ${duplicates.length} duplicate records for user-lesson combination: ${key}`);
        });
        setSnackbarMessage(`Se encontraron ${totalDuplicates} registros duplicados. Se muestran todos para revisión manual.`);
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }

      // Special logging for the test case
      const testUserID = '24a8d4a8-d0f1-70b9-ab66-df82505b6455';
      const testLessonID = '16e0677b-0b1b-4255-a87f-9c965c97b79c';
      const testRecords = userLessonsData.filter(ul => ul.userID === testUserID && ul.lessonID === testLessonID);
      if (testRecords.length > 0) {
        console.log(`🔍 TEST CASE - Found ${testRecords.length} records for test user ${testUserID} + lesson ${testLessonID} (Evaluación 2):`);
        testRecords.forEach((record, idx) => {
          console.log(`  ${idx + 1}. ID: ${record.id}, Progress: ${record.progress}%, Updated: ${record.updatedAt}`);
        });
      }

      // Combine user lessons with lesson titles and mark duplicates
      const enrichedUserLessons = userLessonsData.map(userLesson => {
        const key = `${userLesson.userID}-${userLesson.lessonID}`;
        const duplicates = duplicateGroups[key] || [userLesson];
        const isDuplicate = duplicates.length > 1;
        const isNewest = isDuplicate && duplicates[0].id === userLesson.id; // First in sorted array is newest
        
        return {
          ...userLesson,
          lessonTitle: lessonTitleMap[userLesson.lessonID] || 'Lección no encontrada',
          isDuplicate,
          isNewest,
          duplicateCount: duplicates.length
        };
      });

  setUserLessons(enrichedUserLessons);
  setTotalRecords(enrichedUserLessons.length);
      
      // Set pagination state - when filtering client-side, we might not get a full page
      // so we need to continue loading if we have fewer results than expected and there's a nextToken
  // Mark all data as loaded
  setIsAllDataLoaded(true);
      
      // If we're filtering and got fewer than expected results, but there's still a nextToken,
      // we should continue loading to try to fill the page
  // When using server-side filtering we rely directly on nextToken; no recursive auto-fill needed.

    } catch (error) {
      console.error("Error searching user lessons:", error);
      setSnackbarMessage("Error al buscar las lecciones de usuarios.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  // handleLoadMore removed (all records fetched in one search)

  const handleClearFilters = () => {
    setSearchFilters({
      firstName: '',
      lastName: '',
      email: '',
      lessonID: ''
    });
    setUserLessons([]);
    setHasSearched(false);
  setIsAllDataLoaded(true);
  setTotalRecords(0);
  // pagination state removed
    setSearchCriteriaModified(false);
    setCurrentSearchFilters({ firstName: '', lastName: '', email: '', lessonID: '' });
    setLastSearchCriteria(null);
    
    setSnackbarMessage("Filtros de búsqueda limpiados.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBack = () => {
    navigate('/settings');
  };

  const handleOpenModal = (userLessonId) => {
    setUserLessonToDelete(userLessonId);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setUserLessonToDelete(null);
  };

  const handleConfirmDelete = async () => {
    const client = generateClient();
    try {
      await client.graphql({
        query: deleteUserLesson,
        variables: { input: { id: userLessonToDelete } },
      });
      // Remove from state instead of reloading to save RCUs
      setUserLessons((prevUserLessons) => 
        prevUserLessons.filter((userLesson) => userLesson.id !== userLessonToDelete)
      );
      // Update total records count
      setTotalRecords(prev => Math.max(0, prev - 1));
      setSnackbarMessage("Lección de usuario eliminada exitosamente.");
      setSnackbarSeverity("success");
    } catch (error) {
      console.error("Error deleting user lesson:", error);
      setSnackbarMessage("Error al eliminar la lección de usuario.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
      handleCloseModal();
    }
  };

  const handleUpdateProgress = async (userLessonId, newProgress) => {
    const client = generateClient();
    try {
      await client.graphql({
        query: updateUserLesson,
        variables: { 
          input: { 
            id: userLessonId, 
            progress: newProgress 
          } 
        },
      });
      // Update the local state instead of reloading to save RCUs
      setUserLessons((prevUserLessons) => 
        prevUserLessons.map((userLesson) => 
          userLesson.id === userLessonId 
            ? { ...userLesson, progress: newProgress }
            : userLesson
        )
      );
      setSnackbarMessage("Progreso actualizado exitosamente.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating user lesson progress:", error);
      setSnackbarMessage("Error al actualizar el progreso.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // New handler functions
  const handleSelectionChange = (newSelectedIds) => {
    setSelectedUserLessonIds(newSelectedIds);
  };

  const handleCreateUserLesson = async (userLessonData) => {
    const client = generateClient();
    try {
      setBulkActionLoading(true);
      await client.graphql({
        query: createUserLesson,
        variables: { input: userLessonData },
      });
      
      setSnackbarMessage("Lección de usuario creada correctamente.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      
      // Refresh the data if we have active search
      if (hasSearched) {
        await handleSearch();
      }
    } catch (error) {
      console.error("Error creating user lesson:", error);
      setSnackbarMessage("Error al crear la lección de usuario.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const client = generateClient();
    setBulkActionLoading(true);
    
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const userLessonId of selectedUserLessonIds) {
        try {
          await client.graphql({
            query: deleteUserLesson,
            variables: { input: { id: userLessonId } },
          });
          successCount++;
        } catch (error) {
          console.error(`Error deleting user lesson ${userLessonId}:`, error);
          errorCount++;
        }
      }

      // Update local state to remove deleted items
      setUserLessons(prevUserLessons => 
        prevUserLessons.filter(ul => !selectedUserLessonIds.includes(ul.id))
      );
      setTotalRecords(prev => Math.max(0, prev - successCount));
      setSelectedUserLessonIds([]);

      // Invalidate all caches since we've potentially affected multiple users
      if (successCount > 0) {
        console.log(`Invalidating all user lesson caches after bulk delete of ${successCount} records`);
        invalidateAllUserLessonCaches();
      }

      // Show result message
      if (errorCount === 0) {
        setSnackbarMessage("Registros eliminados.");
        setSnackbarSeverity("success");
      } else if (successCount === 0) {
        setSnackbarMessage("Error al eliminar los registros.");
        setSnackbarSeverity("error");
      } else {
        setSnackbarMessage(`${successCount} registros eliminados. ${errorCount} fallaron.`);
        setSnackbarSeverity("warning");
      }
    } catch (error) {
      console.error("Error in bulk delete:", error);
      setSnackbarMessage("Error en la eliminación masiva.");
      setSnackbarSeverity("error");
    } finally {
      setBulkActionLoading(false);
      setSnackbarOpen(true);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleBulkStatusChange = async (newProgress) => {
    const client = generateClient();
    setBulkActionLoading(true);
    
    let successCount = 0;
    let errorCount = 0;

    try {
      const updatePromises = selectedUserLessonIds.map(async (userLessonId) => {
        try {
          const updateData = {
            id: userLessonId,
            progress: newProgress
          };

          // If setting to 100% and no finishedAt, set it
          const userLesson = userLessons.find(ul => ul.id === userLessonId);
          if (newProgress === 100 && !userLesson?.finishedAt) {
            updateData.finishedAt = new Date().toISOString();
          }

          await client.graphql({
            query: updateUserLesson,
            variables: { input: updateData },
          });
          successCount++;
        } catch (error) {
          console.error(`Error updating user lesson ${userLessonId}:`, error);
          errorCount++;
        }
      });

      await Promise.all(updatePromises);

      // Update local state
      setUserLessons(prevUserLessons => 
        prevUserLessons.map(ul => 
          selectedUserLessonIds.includes(ul.id) 
            ? { 
                ...ul, 
                progress: newProgress,
                ...(newProgress === 100 && !ul.finishedAt && { finishedAt: new Date().toISOString() })
              }
            : ul
        )
      );
      setSelectedUserLessonIds([]);

      // Invalidate all caches since we've potentially affected multiple users
      if (successCount > 0) {
        console.log(`Invalidating all user lesson caches after bulk status change of ${successCount} records`);
        invalidateAllUserLessonCaches();
      }

      // Show result message
      if (errorCount === 0) {
        setSnackbarMessage("Estados actualizados correctamente.");
        setSnackbarSeverity("success");
      } else if (successCount === 0) {
        setSnackbarMessage("Error al actualizar los estados.");
        setSnackbarSeverity("error");
      } else {
        setSnackbarMessage(`${successCount} estados actualizados. ${errorCount} fallaron.`);
        setSnackbarSeverity("warning");
      }
    } catch (error) {
      console.error("Error in bulk status change:", error);
      setSnackbarMessage("Error en el cambio de estado masivo.");
      setSnackbarSeverity("error");
    } finally {
      setBulkActionLoading(false);
      setSnackbarOpen(true);
      setBulkStatusModalOpen(false);
    }
  };

  return (
    <Box className="manage-user-lessons-container">
      <Box className="manage-user-lessons-header">
        <Box className="header-actions">
          <IconButton onClick={handleBack} className="back-button">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" className="page-title">
            Gestión de Lecciones de Usuarios
          </Typography>
        </Box>
        <Typography variant="body1" className="page-subtitle">
          Visualiza el progreso de todos los usuarios en las lecciones
        </Typography>
      </Box>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 2, mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Filtros de Búsqueda
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Nombre"
              variant="outlined"
              size="small"
              value={searchFilters.firstName}
              onChange={(e) => handleSearchInputChange('firstName', e.target.value)}
              placeholder="Buscar por nombre"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Apellido"
              variant="outlined"
              size="small"
              value={searchFilters.lastName}
              onChange={(e) => handleSearchInputChange('lastName', e.target.value)}
              placeholder="Buscar por apellido"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              size="small"
              value={searchFilters.email}
              onChange={(e) => handleSearchInputChange('email', e.target.value)}
              placeholder="Buscar por email"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Lección</InputLabel>
              <Select
                value={searchFilters.lessonID}
                label="Lección"
                onChange={(e) => handleSearchInputChange('lessonID', e.target.value)}
              >
                <MenuItem value="">
                  <em>Todas las lecciones</em>
                </MenuItem>
                {lessonsOptions.map((lesson) => (
                  <MenuItem key={lesson.id} value={lesson.id}>
                    {lesson.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => handleSearch()}
              disabled={loading}
              color={searchCriteriaModified ? "warning" : "primary"}
              sx={{
                height: '40px',
                minWidth: '120px'
              }}
            >
              {loading ? 'Buscando...' : searchCriteriaModified ? 'Buscar (Actualizar)' : 'Buscar'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={loading}
              color="secondary"
              sx={{
                height: '40px',
                minWidth: '120px',
                '&:hover': {
                  backgroundColor: 'rgba(156, 39, 176, 0.04)',
                }
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
        {!hasSearched && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Ingresa criterios de búsqueda y haz clic en "Buscar" para ver los resultados. 
            Si no especificas filtros, se mostrarán todos los registros ordenados por fecha de actualización (más recientes primero).
          </Typography>
        )}
        {searchCriteriaModified && hasSearched && (
          <Typography variant="body2" sx={{ mt: 2, color: '#ff9800', fontWeight: 'bold' }}>
            ⚠️ Los criterios de búsqueda han cambiado. Haz clic en "Buscar (Actualizar)" para aplicar los nuevos filtros.
          </Typography>
        )}
      </Paper>

      {/* Create User Lesson Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          disabled={bulkActionLoading || loading}
        >
          Agregar / Duplicar Lección de Usuario
        </Button>
      </Box>

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCount={selectedUserLessonIds.length}
        onBulkDelete={() => setBulkDeleteDialogOpen(true)}
        onBulkStatusChange={() => setBulkStatusModalOpen(true)}
        disabled={bulkActionLoading || loading}
      />

      <UserLessonsTable 
        userLessons={userLessons} 
        loading={loading} 
        hasSearched={hasSearched}
        totalRecords={totalRecords}
        isAllDataLoaded={isAllDataLoaded}
        onDeleteUserLesson={handleOpenModal}
        onUpdateProgress={handleUpdateProgress}
        selectedIds={selectedUserLessonIds}
        onSelectionChange={handleSelectionChange}
      />

      <Dialog
        open={open}
        onClose={handleCloseModal}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar este registro de lección de usuario?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Create User Lesson Modal */}
      <CreateUserLessonModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreateUserLesson}
        lessonsOptions={lessonsOptions}
        existingUserLessons={userLessons}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <BulkDeleteConfirmDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedUserLessonIds.length}
      />

      {/* Bulk Status Change Modal */}
      <BulkStatusChangeModal
        open={bulkStatusModalOpen}
        onClose={() => setBulkStatusModalOpen(false)}
        onConfirm={handleBulkStatusChange}
        selectedCount={selectedUserLessonIds.length}
      />
    </Box>
  );
};

export default ManageUserLessons;
